import classNames from "classnames";
import React, { ComponentProps, useRef } from "react";
import { mergeRefs } from "use-callback-ref";

import { useShowDesktopLayout } from "../../hooks/useShowdesktopLayout";

import "./button.css";

export function Button({
	className,
	children,
	variant = "default",
	checked,
	round,
	ref,
	...restProps
}: {
	/**
	 * default: gray button background and border
	 * flat: no background and border
	 * filled: filled with --btn-fill-color
	 * link: no border and no background like a link
	 */
	variant?: "default" | "flat" | "filled" | "link";
	round?: boolean;
	/**
	 * Give it an active state
	 * Only possible with default and flat style
	 */
	checked?: boolean;
	ref?: React.Ref<HTMLButtonElement>;
} & ComponentProps<"button">) {
	// I would prefer we don't need to do this
	const isDesktopLayout = useShowDesktopLayout();
	round ??= !isDesktopLayout;
	const allCaps = !isDesktopLayout;

	const cssClass = classNames("button", className, {
		"button--normal": variant === "default",
		"button--flat": variant === "flat",
		"button--link": variant === "link",
		"button--filled": variant === "filled",

		"button--round": round,
		"button--all-caps": allCaps,
	});

	const innerRef = useRef<HTMLButtonElement>(null);

	if (checked && variant !== "default" && variant !== "flat") {
		throw new Error(
			"Checked state is only supported for 'default' and 'flat' button variants.",
		);
	}

	return (
		<button
			ref={ref ? mergeRefs([ref, innerRef]) : innerRef}
			type="button"
			className={cssClass}
			aria-pressed={checked}
			data-checked={checked ? "" : undefined}
			{...restProps}
		>
			{children}
		</button>
	);
}
Button.displayName = "Button";
